import React, { Component } from "react";
import _ from "lodash";
import loadContent from "./loadContent";
import LoadingBox from "../../common/components/LoadingBox";
import populatePath from "../tiny-functions/populatePath";

import * as api from "../services/api";
import * as propertyTypes from "../propertyTypes";
import createRegex from "../tiny-functions/createRegex";
import cleanLabel from "../tiny-functions/cleanLabel";

import { ApiError, ApiTellUser } from "../services/error";
import platform from "../../platform";

import store from "../../store";
import { showAlert } from "../services/errorAlert/reducers";

const loadForm = function(path, InnerComponent) {
  class WrapperComponent extends Component {
    constructor(props) {
      super(props);
      this.state = {
        isLoading: true,
        validForm: false,
        errorMessage: "",
        errorMissingRequired: false,
        errorInvalid: false,
        alert: null
      };
    }

    componentDidMount() {
      if (this.props.setIsValidated != null) {
        this.props.setIsValidated(this.isFormValid);
      }
      if (this.props.setSendState != null) {
        this.props.setSendState(this.getFormState);
      }

      const schema = this.props.getSchema();
      const newState = { ...this.state };

      const oName = path
        .split("/")
        .slice(-1)[0]
        .split(".")[0];

      const objectID = this.props.ids[oName];

      _.forOwn(schema, (propValue, propName) => {
        if (objectID != null && Number(objectID) !== 0) {
          newState[propName] = this.props.getData(propName, "value");
        } else {
          //This empty string could be more elaborated to match the prop type (boolean, number ...)
          newState[propName] = "";
        }

        newState[propName + "State"] = "";
        newState[propName + "Mounted"] = false;
        newState[propName + "Initialized"] = false;
        if (propValue.type === propertyTypes.OPEN_INPUT) {
          newState[propName + "Regex"] = createRegex(propValue.regex);
        }

        if (newState[propName] == null) {
          newState[propName] = "";
        }
      });

      if (platform === "web") {
        if (window != null && "sessionStorage" in window) {
          const persistentState = window.sessionStorage.getItem(
            oName + "PersistentState"
          );
          if (persistentState != null) {
            let restoredState = JSON.parse(persistentState);
            var allNewState = {};
            _.forOwn(restoredState, (value, key) => {
              if (!key.includes("Regex")) {
                allNewState[key] = value;
              }
            });
          }
        }
      }

      _.forOwn(schema, (propValue, propName) => {
        if (
          this.props.autoFillCustomValue != null &&
          propName in this.props.autoFillCustomValue &&
          this.props.autoFillCustomValue[propName] != null
        ) {
          //Overwrite the initial value
          newState[propName] = this.props.autoFillCustomValue[propName];
          newState[propName + "Initialized"] = true;
        }
      });

      if (platform === "web") {
        this.persistorID = setInterval(() => {
          if (window != null && "sessionStorage" in window) {
            window.sessionStorage.setItem(
              oName + "PersistentState",
              JSON.stringify(this.state)
            );
          }
        }, 3000);

        this.beforeUnloadHandler = function(e) {
          var confirmationMessage =
            "You are about to leave this page. Do you confirm? ";
          (e || window.event).returnValue = confirmationMessage; //Gecko + IE
          return confirmationMessage; //Gecko + Webkit, Safari, Chrome etc.
        };
        window.addEventListener("beforeunload", this.beforeUnloadHandler);
      }
      newState.isLoading = false;
      this.setState({ ...newState, ...allNewState });
      this.initiatedState = { ...newState, ...allNewState };
    }

    componentWillUnmount() {
      if (platform === "web") {
        window.removeEventListener("beforeunload", this.beforeUnloadHandler);
        clearInterval(this.persistorID);
      }
    }

    setupInput = (propName, initialValue) => {
      const newState = { ...this.state };
      newState[propName + "State"] = "";
      newState[propName + "Mounted"] = false;
      newState[propName + "Initialized"] = false;
      newState[propName] = "";
      this.setState(newState);
    };

    findErrorMessage = () => {
      if (this.state.errorMessage !== "") {
        return this.state.errorMessage;
      } else if (this.state.errorMissingRequired) {
        return "Missing mandatory fields.";
      } else if (this.state.errorInvalid) {
        return "Some inputs have invalid format.";
      } else {
        return "Please review form fields.";
      }
    };

    post = async (
      onSuccessCallback = () => null,
      shouldLoadAfterPost = true,
      preparePostPayload = null
    ) => {
      if (!this.isFormValid()) {
        store.dispatch(showAlert(this.findErrorMessage()));
        return false;
      }

      this.setState({ isLoading: true });

      const postURL = this.getURL();

      const payload =
        typeof preparePostPayload === "function"
          ? preparePostPayload(this.getFormState())
          : this.getFormState();

      try {
        var res = await api.post(postURL, payload);

        if (res.error === 1 || res.error === "1") {
          const errorMessage = res.message
            ? res.message
            : "Something went wrong. Please try again later.";
          store.dispatch(showAlert(errorMessage));
          throw new ApiError(errorMessage);
        }
      } catch (e) {
        if (e instanceof ApiError || e instanceof ApiTellUser) {
          console.log(e.message);
          this.setState({
            formValid: false,
            errorMessage: e.message,
            isLoading: false
          });
          return false;
        } else {
          this.setState({
            formValid: false,
            errorMessage: "Something went wrong. Please try again later.",
            isLoading: false
          });
          throw e;
        }
      }

      if (shouldLoadAfterPost) {
        await api.load(postURL);
      }

      this.setState({
        ...this.initiatedState,
        errorMessage: "",
        isLoading: true
      });

      const oName = path
        .split("/")
        .slice(-1)[0]
        .split(".")[0];

      if (platform === "web") {
        if (window != null && "sessionStorage" in window) {
          window.sessionStorage.removeItem(oName + "PersistentState");
        }
      }

      if (onSuccessCallback != null) {
        onSuccessCallback();
      }

      return res;
    };

    cancel = () => {
      const oName = path
        .split("/")
        .slice(-1)[0]
        .split(".")[0];

      if (platform === "web") {
        if (window != null && "sessionStorage" in window) {
          window.sessionStorage.removeItem(oName + "PersistentState");
        }
      }
    };

    validateField = (value, propName, type) => {
      if (type === propertyTypes.OPEN_INPUT) {
        const regex = this.state[propName + "Regex"];
        return regex != null && typeof regex.test === "function"
          ? regex.test(value)
          : false;
      } else if (type === propertyTypes.SELECT_OPTION) {
        return true;
        /*
        if (this.getDropdownOptions(propName) == null) {
          return true;
        }
        */
      }
      if (type === propertyTypes.BOOLEAN) {
        if (value === true || value === false) {
          return true;
        } else {
          return false;
        }
      } else {
        throw new Error("Unknown type [" + type + "] on validateField");
      }
    };

    change = (theValue, thePropName) => {
      const schema = this.props.getSchema();
      const newState = { ...this.state };

      newState.validForm = true;
      newState.errorMissingRequired = false;
      newState.errorInvalid = false;
      newState.errorMessage = "";

      _.forOwn(schema, (propProps, propName) => {
        let type = propProps.type;
        let value = newState[propName];

        newState[propName + "State"] = "";

        if (propName === thePropName) {
          value = theValue;
          newState[propName + "Initialized"] = true;
        } else {
          if (this.state[propName + "Initialized"] !== true) {
            return;
          }
        }

        if (!this.isRequired(propName) && value === "") {
          newState[propName + "State"] = "";
        } else {
          if (this.validateField(value, propName, type)) {
            newState[propName + "State"] = "Success";
          } else {
            newState[propName + "State"] = "Error";
          }
        }

        newState[propName] = value;
      });

      this.setState(newState);

      return true;
    };

    changeMany = values =>
      new Promise(resolve => {
        const schema = this.props.getSchema();
        const newState = { ...this.state };

        newState.validForm = true;
        newState.errorMissingRequired = false;
        newState.errorInvalid = false;
        newState.errorMessage = "";

        _.forOwn(values, (value, propName) => {
          if (!(propName in schema)) {
            return;
          }

          newState[propName] = value;
          newState[propName + "Initialized"] = true;
          newState[propName + "State"] = "";
        });

        this.setState(newState, resolve);
      });

    setPropMounted = (propName, mounted) => {
      this.setState({ [propName + "Mounted"]: mounted });
    };

    getState = propName => this.state[propName + "State"];

    getAllState = () => this.state;

    getMounted = propName => this.state[propName + "Mounted"];

    getValue = propName => this.state[propName];

    getLabel = propName => {
      const schema = this.props.getSchema();
      if (Array.isArray(schema[propName].label)) {
        for (let rule of schema[propName].label) {
          if (this.getValue(rule[1]) === rule[2]) {
            return cleanLabel(rule[0]);
          }
        }
      } else {
        return cleanLabel(schema[propName].label);
      }
    };

    getErrorMessage = propName => {
      const schema = this.props.getSchema();
      return schema[propName].errorMessage;
    };

    getDropdownOptions = propName => {
      const schema = this.props.getSchema();
      return schema[propName].dropdownOptions;
    };

    getCodebookDropdownOptions = async codebookName => {
      return await this.props.getCodebook(codebookName);
    };

    getFormState = () => {
      const schema = this.props.getSchema();
      const allStates = {};
      _.forOwn(schema, (propValue, propName) => {
        if (
          this.state[propName] != null &&
          this.state[propName + "Initialized"]
        ) {
          allStates[propName] = this.state[propName];
        }
      });
      if (this.props.extraPostValues != null) {
        return {
          ...allStates,
          ...this.props.extraPostValues
        };
      }
      return allStates;
    };

    getFormErrorState = () => {
      return {
        formValid: this.state.validForm,
        errorMessage: this.state.errorMessage,
        errorMissingRequired: this.state.errorMissingRequired,
        errorInvalid: this.state.errorInvalid
      };
    };

    getURL = () => {
      const oName = path
        .split("/")
        .slice(-1)[0]
        .split(".")[0];

      const ids = { ...this.props.ids };
      if (Number(ids[oName]) === 0) {
        delete ids[oName];
      }
      return populatePath(path, ids);
    };

    isRequired = propName => {
      const schema = this.props.getSchema();
      if (
        !("requiredRules" in schema[propName]) ||
        !Array.isArray(schema[propName].requiredRules)
      ) {
        return schema[propName].required;
      }

      let required = false;

      for (let rule of schema[propName].requiredRules) {
        let triggeredRule = false;
        if (rule[2] === "notEmpty") {
          if (this.state[rule[1]].length > 0) {
            triggeredRule = true;
            break;
          }
        } else if (rule[2] === "equals") {
          if (rule[3].indexOf(this.state[rule[1]]) !== -1) {
            triggeredRule = true;
          }
        }
        if (triggeredRule && rule[0] === "requiredif") {
          required = true;
        }

        if (triggeredRule) {
          break;
        }
      }

      return required;
    };

    isFormValid = () => {
      const schema = this.props.getSchema();
      let validForm = true;
      const newState = {
        ...this.state
      };
      _.forOwn(schema, (propValue, propName) => {
        if (this.state[propName + "Mounted"] === false) {
          return;
        }
        if (this.state[propName] === "" && this.isRequired(propName)) {
          newState.errorMissingRequired = true;
          validForm = false;
          newState[propName + "State"] = "Error";
        } else if (this.state[propName + "State"] === "Error") {
          newState.errorInvalid = true;
          validForm = false;
        }
      });
      if (validForm === false) {
        this.setState(newState);
      } else {
        newState.errorMissingRequired = false;
        newState.errorInvalid = false;
      }
      this.setState({ validForm });
      return validForm;
    };

    render() {
      const { setIsValidated, setSendState, ...passThroughProps } = this.props;
      if (this.state.isLoading) {
        return <LoadingBox />;
      } else {
        const connectForm = {
          change: this.change,
          changeMany: this.changeMany,
          cancel: this.cancel,
          post: this.post,
          getMounted: this.getMounted,
          getSchema: this.props.getSchema,
          getState: this.getState,
          getAllState: this.getAllState,
          getValue: this.getValue,
          getData: this.props.getData,
          getLabel: this.getLabel,
          setupInput: this.setupInput,
          getErrorMessage: this.getErrorMessage,
          getFormErrorState: this.getFormErrorState,
          getDropdownOptions: this.getDropdownOptions,
          getCodebookDropdownOptions: this.getCodebookDropdownOptions,
          isRequired: this.isRequired,
          getFormState: this.getFormState,
          isFormValid: this.isFormValid,
          setPropMounted: this.setPropMounted,
          getURL: this.getURL
        };
        return (
          <div>
            {this.state.alert}
            <InnerComponent connectForm={connectForm} {...passThroughProps} />
          </div>
        );
      }
    }
  }
  return loadContent(path, WrapperComponent);
};

export default loadForm;
